<script setup lang="ts">
import type { PostingComment } from '~/types/postings'

defineOptions({ name: 'Trip' })

const route = useRoute()
const authStore = useAuthStore()
const { getTrip } = useTripService()
const { getSectionPolyline } = useSectionService()
const { getComments } = useCommentsService()
const { getBike } = useGarageService()

const tripId = route.params.id as string

// Captured before the first await: without `experimental.asyncContext` the Nuxt
// context (and thus the request event) is no longer reliably available after an
// await, which would silently turn the 404 status below into a no-op.
const requestEvent = useRequestEvent()

// If no trip id specified, redirect to the user's roadbook.
if (!route.params.id) {
  navigateTo('/roadbook')
}

// The trip is awaited so a public trip is part of the server-rendered HTML (SEO).
// The handler never throws: a failure simply means "not loadable in this
// context" (e.g. a private trip fetched server-side, where there is no user
// token). The not-found handling below decides what that means.
const { data: trip, refresh } = await useAsyncData(`trip-${tripId}`, () =>
  getTrip(tripId).catch(() => null)
)

// The server render runs without the user's token, so private trips can't be
// loaded here. Always emit a soft 404 (status only, no throw) so crawlers see a
// not-found while the page is still shipped — the client then decides whether to
// retry with the user's token or show the 404 page.
if (import.meta.server && !trip.value && requestEvent) {
  setResponseStatus(requestEvent, 404)
}

// Auth-dependent resolution runs after hydration so the first client paint
// matches the anonymous server render (avoiding hydration mismatches). The server
// render is always anonymous, so for a logged-in user we re-fetch with their
// token — which also loads a private trip that was unreachable server-side. A
// trip still missing afterwards is a genuine 404.
//
// `onNuxtReady`, not `onMounted`: while the app is still hydrating,
// `useAsyncData`'s `refresh()` short-circuits to the cached server payload
// without running the handler. `onNuxtReady` fires once hydration is done.
onNuxtReady(async () => {
  if (authStore.session_data.user) {
    await refresh()
  }

  if (!trip.value) {
    showError({ statusCode: 404, statusMessage: 'Page Not Found' })
  }
})

// Secondary information is loaded lazily so it never blocks the trip render. The
// `watch` re-runs the dependent fetches once the trip resolves on the client
// (private-trip retry), since the server-side result was cached as empty.
const tripLoaded = () => trip.value?.id

const { data: sectionPolylineData } = useLazyAsyncData(
  `trip-polyline-${tripId}`,
  async () => (trip.value ? await getSectionPolyline(trip.value.main_section.id) : null),
  { watch: [tripLoaded] }
)

const sectionPolyline = computed(() => sectionPolylineData.value?.polyline)

// Comments require the user's token and have no SEO value, so they are fetched
// client-only rather than reusing the anonymous server payload.
const { data: comments } = useLazyAsyncData(
  `trip-comments-${tripId}`,
  async () => {
    if (!authStore.session_data.user) return [] as PostingComment[]

    return ((await getComments(`trips/${tripId}/comments`)) ?? []) as PostingComment[]
  },
  { server: false, watch: [tripLoaded] }
)

// The bike endpoint requires auth, so it is fetched client-only (like comments)
// and skipped for logged-out users; a failure here never breaks the page.
const { data: bike } = useLazyAsyncData(
  `trip-bike-${tripId}`,
  async () => {
    if (!trip.value || trip.value.bike_id == null || !authStore.session_data.user) return null

    try {
      return (await getBike(trip.value.bike_id)) ?? null
    } catch (err) {
      console.error('Failed to load trip bike', err)

      return null
    }
  },
  { server: false, watch: [tripLoaded] }
)
</script>

<template>
  <TripDetails
    v-if="trip"
    v-model:trip="trip"
    :comments="comments ?? []"
    :section-polyline="sectionPolyline"
    :bike="bike"
  />
</template>
